// src/Components/FavoriteButton.jsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../context/userContext';
import { addToFavorites, removeFromFavorites, isInFavorites } from '../utils/favoritesUtils';

const FavoriteButton = ({ product, size = 'medium' }) => {
  const { id: userId, loading, initialized, telegramUser } = useUser();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState(null);

  // Используем telegramUser.id как fallback
  const effectiveUserId = userId || telegramUser?.id?.toString();

  // Оптимистичное обновление состояния
  const [optimisticFavorite, setOptimisticFavorite] = useState(null);

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!initialized || !effectiveUserId || !product?.id) {
        setIsChecking(false);
        return;
      }

      try {
        const status = await isInFavorites(effectiveUserId, product.id);
        setIsFavorite(status);
        setOptimisticFavorite(null);
      } catch (err) {
        console.error('Error checking favorite status:', err);
      } finally {
        setIsChecking(false);
      }
    };

    checkFavoriteStatus();
  }, [effectiveUserId, product, initialized]);

  const handleToggleFavorite = async () => {
    if (!initialized || !effectiveUserId || !product?.id) return;

    // Оптимистичное обновление UI
    const newFavoriteState = !isFavorite;
    setOptimisticFavorite(newFavoriteState);
    setIsFavorite(newFavoriteState);

    try {
      if (newFavoriteState) {
        await addToFavorites(effectiveUserId, product);
      } else {
        await removeFromFavorites(effectiveUserId, product.id);
      }
      setOptimisticFavorite(null);
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Откатываем оптимистичное обновление при ошибке
      setOptimisticFavorite(null);
      setIsFavorite(!newFavoriteState);
      setError('Ошибка при изменении избранного');
      
      // Автоматически скрываем ошибку через 3 секунды
      setTimeout(() => setError(null), 3000);
    }
  };

  const sizes = {
    small: 'w-6 h-6',
    medium: 'w-8 h-8',
    large: 'w-10 h-10'
  };

  // Определяем отображаемое состояние
  const displayFavorite = optimisticFavorite !== null ? optimisticFavorite : isFavorite;
  
  // Кнопка активна даже при проверке начального состояния
  const isDisabled = !initialized || !effectiveUserId;

  return (
    <div className="relative">
      <button
        onClick={handleToggleFavorite}
        disabled={isDisabled}
        className={`
          ${sizes[size]} 
          flex items-center justify-center 
          rounded-full 
          transition-all duration-200
          ${isDisabled 
            ? 'opacity-50 cursor-not-allowed' 
            : 'cursor-pointer hover:scale-110 active:scale-95'
          }
          ${isChecking ? 'opacity-70' : ''}
        `}
        aria-label={displayFavorite ? "Удалить из избранного" : "Добавить в избранное"}
        title={displayFavorite ? "Удалить из избранного" : "Добавить в избранное"}
      >
        {/* Анимация пульсации при загрузке начального состояния */}
        {isChecking && (
          <div className="absolute inset-0 animate-pulse bg-gray-400/20 rounded-full"></div>
        )}
        
        {/* Основная иконка сердца */}
        <svg 
          className={`
            w-6 h-6 
            transition-all duration-300
            ${displayFavorite 
              ? 'fill-red-500 text-red-500 scale-110' 
              : 'fill-gray-300 text-gray-300 hover:fill-red-200 hover:text-red-200'
            }
            ${isChecking ? 'opacity-50' : ''}
          `} 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
        
        {/* Анимация добавления/удаления */}
        {optimisticFavorite !== null && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`
              w-8 h-8 rounded-full
              animate-ping
              ${displayFavorite ? 'bg-red-400/30' : 'bg-gray-400/30'}
            `}></div>
          </div>
        )}
      </button>
      
      {/* Убрана всплывающая подсказка при наведении */}
      
      {error && (
        <div className="absolute top-full right-0 mt-1 bg-red-100 text-red-600 text-xs px-2 py-1 rounded whitespace-nowrap animate-fadeIn">
          {error}
        </div>
      )}
    </div>
  );
};

export default FavoriteButton;